import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { LoaderService, MD_MODAL_PARAMS, SHOW_SPINNER } from 'up';
import { InterceptorErrorComponent } from './interceptor-error/interceptor-error.component';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  constructor(
    private loaderService: LoaderService,
    private modalService: NgbModal,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (this._isTranslationAssetRequest(request.url)) {
      return next.handle(request);
    }

    const jwtToken = this._getJwtFromSessionStorage();
    const requestWithHeaders = this._buildRequestWithHeaders(request, jwtToken);
    const shouldHideSpinner = this._shouldHideSpinner(requestWithHeaders);

    if (!shouldHideSpinner) {
      this.loaderService.show();
    }

    return next.handle(requestWithHeaders).pipe(
      tap((response) => this._updateJwtFromResponse(response)),
      catchError((error: HttpErrorResponse) => this._handleHttpError(error)),
      finalize(() => {
        this.loaderService.hide();
      }),
    );
  }

  private _isTranslationAssetRequest(url: string): boolean {
    return url.includes('assets/i18n') || url.includes('assets/quid-i18n');
  }

  private _getJwtFromSessionStorage(): string {
    try {
      const token = sessionStorage.getItem('JWT');
      return token && token !== '' ? token : '';
    } catch {
      return '';
    }
  }

  private _buildRequestWithHeaders(
    request: HttpRequest<unknown>,
    jwtToken: string,
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${jwtToken}`,
        'Access-Control-Allow-Origin': '*',
      },
      url: request.url,
    });
  }

  private _shouldHideSpinner(request: HttpRequest<unknown>): boolean {
    return (
      !request.context.get(SHOW_SPINNER) ||
      request.url.includes('activity/assessment/item/update') ||
      request.url.includes('activity/assessment/position')
    );
  }

  private _updateJwtFromResponse(response: HttpEvent<unknown>): void {
    if (!(response instanceof HttpResponse)) {
      return;
    }

    const authHeader = response.headers.get('authorization');
    if (!authHeader) {
      return;
    }

    sessionStorage.setItem('JWT', authHeader.split(' ')[1] || '');
  }

  private _handleHttpError(error: HttpErrorResponse): Observable<never> {
    this.modalService.dismissAll();

    const tokenModalContent = this._getTokenErrorModalContent(
      error?.error?.errorCode,
    );

    if (tokenModalContent) {
      this._openTokenErrorModal(
        tokenModalContent.title,
        tokenModalContent.body,
      );
    }

    return throwError(() => error);
  }

  private _getTokenErrorModalContent(
    errorCode: string | undefined,
  ): { title: string; body: string } | null {
    switch (errorCode) {
      case 'TOKEN_INVALID':
        return {
          title: 'GENERIC.MODAL.INVALID_TOKEN_HEADER',
          body: 'GENERIC.MODAL.INVALID_TOKEN_MSG',
        };
      case 'TOKEN_EXPIRED':
        return {
          title: 'GENERIC.MODAL.EXPIRED_TOKEN_HEADER',
          body: 'GENERIC.MODAL.EXPIRED_TOKEN_MSG',
        };
      default:
        return null;
    }
  }

  private _openTokenErrorModal(title: string, body: string): void {
    const modalRef = this.modalService.open(
      InterceptorErrorComponent,
      MD_MODAL_PARAMS,
    );
    modalRef.componentInstance.modalTitle = title;
    modalRef.componentInstance.modalBody = body;
  }
}
